import {Catch, Trace, Error} from "../../Utilities";
import {TimeStretchCode} from "./GeminiTimeStretch";

const SAMPLE_RATE = 24000;
const FADEIN_SECONDS = 0.3;
const FADEOUT_SECONDS = 1;
const FADEIN_SAMPLES = Math.floor(FADEIN_SECONDS * SAMPLE_RATE);
const FADEOUT_SAMPLES = Math.floor(FADEOUT_SECONDS * SAMPLE_RATE);

const WORKLET_CODE = `
    ${TimeStretchCode()}

    class BufferWorklet extends AudioWorkletProcessor {
        constructor(options) {
            super();
            this.interrupted = false;
            this.fadeoutSamples = options.processorOptions.fadeoutSamples;
            this.stretch = new PitchPreservingTimeStretch(
                options.processorOptions.speed,
                sampleRate);
            this.port.onmessage = (e) => this.handleMessage(e.data);
        }

        handleMessage(data) {
            switch (data.type) {
                case 'chunk':
                    this.add(data.chunk);
                    break;
                case 'speed':
                    this.stretch.setSpeed(data.speed);
                    break;
                case 'interrupt':
                    this.interrupt();
                    break;
            }
        }

        add(chunk) {
            this.interrupted = false;
            this.stretch.add(chunk);
        }

        interrupt() {
            if (this.interrupted) return;
            this.interrupted = true;
            this.stretch.interrupt(this.fadeoutSamples);
        }

        process(inputs, outputs) {
            this.stretch.process(outputs[0][0]);
            return true;
        }
    }
    registerProcessor('buffer-worklet', BufferWorklet);
`;

let Context: AudioContext | null = null;
let WorkletNode: AudioWorkletNode | null = null;
let Initialization: Promise<void> | null = null;
let Deinitialization: Promise<void> | null = null;
let Timestamp = 0;
let Speed = 1;

function Listeners(list: any): Function[] {
    if (list == null) return [];
    if (typeof list === "function") return [list];
    if (Array.isArray(list)) return list.filter(x => typeof x === "function");
    if (typeof list[Symbol.iterator] === "function") {
        return Array.from(list).filter(x => typeof x === "function");
    }
    if (typeof list === "object") {
        return Object.values(list).filter(x => typeof x === "function");
    }
    return [];
}

async function LoadWorklet(): Promise<void> {
    const blob = new Blob([WORKLET_CODE], {type: "application/javascript"});
    const url = URL.createObjectURL(blob);
    try {
        await Context!.audioWorklet.addModule(url);
    }
    finally {
        URL.revokeObjectURL(url);
    }
}

function CreateWorklet(speed: number): AudioWorkletNode {
    return new AudioWorkletNode(Context!, "buffer-worklet", {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: {
            speed,
            fadeoutSamples: FADEOUT_SAMPLES
        }
    });
}

async function Initialize(speed: number): Promise<void> {
    SetSpeed(speed);
    if (Initialization) return Initialization;
    if (Deinitialization) await Deinitialization;
    
    Trace("Adio output initializing...");
    
    Initialization = (async () => {
        Context = new AudioContext({sampleRate: SAMPLE_RATE});
        await LoadWorklet();
        WorkletNode = CreateWorklet(Speed);
        WorkletNode.connect(Context.destination);
        await Notify();
    })();
    
    Initialization.then(() => Trace("Audio output initialization completed"));
    Initialization.catch(e => Error("Audio output initialization failed", e));
    
    return Catch(Initialization);
}

async function Notify() {
    const wnd: any = window;
    const stream = {
        context: Context,
        node: WorkletNode
    };

    if (wnd.audioMediator != null) {
        wnd.audioMediator.stream = stream;
        wnd.audioMediator.listeners ??= [];
        
        for (let l of Listeners(wnd.audioMediator.listeners)) {
            l(stream);
        }
    } else {
        wnd.audioMediator = {
            stream,
            listeners: []
        };
    }
}

function Deinitialize() {
    if (!Initialization) return;
    const initialization = Initialization;
    Initialization = null;
    
    Trace("Audio output deinitializing...");
    
    Deinitialization = (async () => {
        try {
            await initialization;
            
            Interrupt();
            
            if (WorkletNode) {
                WorkletNode.disconnect();
                WorkletNode = null;
            }
            if (Context) {
                Context.close().catch();
                Context = null;
            }
        }
        catch (e) {
            Error(e);
        }
        finally {
            Trace("Audio output deinitialization completed");
        }
    })();
}

function Play(chunk: ArrayBuffer) {

    // Gemini live has 'clicks' in the 
    // beginning of the stream (at least in version 12-2025).
    // So cut first 2000 samples in the first chunk.
    // Probably, remove it in the future.
    const now = Date.now();
    const elapsed = now - Timestamp;
    const beginning = Timestamp === 0 || elapsed > 1000;
    Timestamp = now;
 
    // Expect PCM16LE mono 24000 Hz chunks stream
    const pcm16 = new Int16Array(chunk);
    const float32 = new Float32Array(pcm16.length);    
    const fadein = Math.min(pcm16.length, FADEIN_SAMPLES);
    
    for (let i = 0; i < pcm16.length; i++) {
        if (beginning && i < fadein) float32[i] = (pcm16[i] / 32768.0) * (i / fadein);        
        else float32[i] = pcm16[i] / 32768.0;
    }

    WorkletNode!.port.postMessage({type: "chunk", chunk: float32});
}

function SetSpeed(speed: number): void {
    Speed = Number.isFinite(speed) && speed > 0 ? speed : 1;
    WorkletNode?.port.postMessage({type: "speed", speed: Speed});
}

function Interrupt(): void {
    if (!WorkletNode) return;
    WorkletNode.port.postMessage({type: "interrupt"});
    Trace("Interrupting audio output");
}

export const GeminiLiveOutput = {
    Play,
    Interrupt,
    Initialize,
    Deinitialize,
    SetSpeed
};
